import { useState, useEffect } from 'react';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('useAvailableChannels', 'encompass-prospects');

export interface ChannelCount {
  channel: string;
  count: number;
}

/**
 * Fetches available loan channels with counts.
 * Channels are: Brokered, Banked - Retail, Banked - Wholesale, Correspondent
 * Respects excludedFolders to match the filtered loan count.
 */
export function useAvailableChannels(
  organizationId: string | undefined,
  excludedFolders: string[] = []
) {
  const [channels, setChannels] = useState<ChannelCount[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!organizationId) {
      setChannels([]);
      setIsLoading(false);
      return;
    }

    const fetchChannels = async () => {
      setIsLoading(true);
      try {
        let query = supabase
          .from('encompass_loans')
          .select('channel')
          .eq('organization_id', organizationId)
          .eq('deleted_from_encompass', false)
          .neq('loan_folder', 'Transferred Data')
          .not('channel', 'is', null);

        // Apply excluded folders filter
        for (const folder of excludedFolders) {
          query = query.neq('loan_folder', folder);
        }

        const { data, error } = await query;

        if (error) {
          log.error('Failed to fetch channels', { organizationId }, error);
          return;
        }

        // Also count NULL channels separately
        let nullQuery = supabase
          .from('encompass_loans')
          .select('id', { count: 'exact', head: true })
          .eq('organization_id', organizationId)
          .eq('deleted_from_encompass', false)
          .neq('loan_folder', 'Transferred Data')
          .is('channel', null);

        for (const folder of excludedFolders) {
          nullQuery = nullQuery.neq('loan_folder', folder);
        }

        const { count: nullCount } = await nullQuery;

        // Count by channel
        const channelCounts = new Map<string, number>();
        for (const row of data || []) {
          const channel = row.channel;
          if (channel) {
            channelCounts.set(channel, (channelCounts.get(channel) || 0) + 1);
          }
        }

        // Add Unknown for NULL channels
        if (nullCount && nullCount > 0) {
          channelCounts.set('Unknown', nullCount);
        }

        // Convert to array and sort by count
        const result: ChannelCount[] = Array.from(channelCounts.entries())
          .map(([channel, count]) => ({ channel, count }))
          .sort((a, b) => b.count - a.count);

        log.info('Loaded channels', {
          organizationId,
          channelCount: result.length
        });

        setChannels(result);
      } catch (err) {
        log.error('Error fetching channels', { organizationId }, err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchChannels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, JSON.stringify(excludedFolders)]);

  return { channels, isLoading };
}
